<template>
  <div class="dimension-section">
    <div class="headline">
      <h2 class="h4">
        {{ $tv('dimensionsInstallation', 'product') }}
      </h2>
      <ToggleSwitchSquare
        :is-checked="showInchValues"
        :label-left="$tv('metricInchToggleLeftText','product')"
        :label-right="$tv('metricInchToggleRightText','product')"
        @change-value="toggleShowInchValues"
      />
    </div>
    <div class="dimensions-container">
      <div
        class="dimensions-container__dimensions-img-container"
        :class="{'no-events': isFallbackImage}"
        @click="!isFallbackImage && (showDimensionsPopup = true)"
      >
        <img
          :src="installationImage.url"
          :alt="installationImage.alternativeText"
        >
        <p
          v-show="!isFallbackImage"
          class="click-image"
        >
          {{ $tv('clickImage','product') }}
        </p>
      </div>
      <div class="dimensions-container__dimensions-details">
        <div
          v-for="(dimension, index) in dimensions"
          :key="index"
        >
          <span
            class="dimension-label"
            v-html="dimension.label"
          />
          <span
            class="dimension-measurement"
            v-text="dimension.value"
          />
        </div>
      </div>
    </div>
    <div v-if="productAttributes.length > 0">
      <h3 class="h5">
        {{ $tv('additionalAttributes', 'product') }}
      </h3>
      <div
        class="additional-attributes-section"
      >
        <div
          class="attributes"
          v-for="attr, i in productAttributes"
          :key="i"
        >
          <p class="dimension-label">
            {{ attr.attributeIdentifier }}:
          </p><p>{{ attr.value }}</p>
        </div>
      </div>
    </div>
    <PopupBase
      :show="showDimensionsPopup"
      @close-pop-up="showDimensionsPopup = false"
    >
      <div class="details">
        <div class="headline">
          <HeadLine
            :headline="$tv('dimensionsInstallationDetails', 'product')"
            class="dimensions-headline"
          />
        </div>
        <div class="popup-switch">
          <ToggleSwitchSquare
            :is-checked="showInchValues"
            :label-left="$tv('metricInchToggleLeftText','product')"
            :label-right="$tv('metricInchToggleRightText','product')"
            @change-value="toggleShowInchValues"
          />
        </div>
        <div class="dimensions-container">
          <div
            class="dimensions-container__dimensions-img-container"
            @click="showDimensionsPopup = true"
          >
            <img
              :src="installationImage.url"
              :alt="installationImage.alternativeText"
            >
          </div>
          <div class="dimensions-container__dimensions-details">
            <div
              v-for="(dimension, index) in dimensions"
              :key="index"
            >
              <span
                class="dimension-label"
                v-html="dimension.label"
              />
              <span
                v-text="dimension.value"
              />
            </div>
          </div>
        </div>
        <div class="bottom-section">
          <div
            v-if="productAttributes.length > 0"
            class="additional-attributes-section"
          >
            <HeadLine
              :headline="$tv('additionalAttributes', 'product')"
            />
            <div>
              <div
                class="attributes"
                v-for="attr, i in productAttributes"
                :key="i"
              >
                <span class="dimension-label">{{ attr.attributeIdentifier }}:</span><span>{{ attr.value }}</span>
              </div>
            </div>
          </div>
          <div class="downloads">
            <HeadLine
              :headline="$tv('downloads', 'product')"
            />
            <div class="downloads-container">
              <ul
                v-for="(file, index) in sortedProductDownloads"
                :key="index"
              >
                <li>
                  <a
                    :href="getDownloadLink(file, url)"
                    target="_blank"
                    download
                    class="download-item semi-bold"
                    :title="getAltText(file)"
                  >
                    {{ getFileName(file, $tv) }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PopupBase>
  </div>
</template>

<script>
import HeadLine from './HeadLine.vue'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import {FALLBACK_IMAGE} from '@/assets/js/constants'
import {useItemImageUrlHelper} from '~/composables/item_image_url_helper'
import {useUserStore} from '@/store/user'
import {storeToRefs} from 'pinia'
import {useDownloadFunctions} from '@/composables/download_functions.js'

export default {
  components: {
    HeadLine,
    ToggleSwitchSquare
  },
  props: {
    product: {
      type: Object,
      default() {
        return {}
      }
    },
    productAttributes: {
      type: Array,
      default() {
        return []
      }
    },
    dimensions: {
      type: Array,
      default() {
        return []
      }
    },
    productDownloads: {
      type: Array,
      default() {
        return []
      }
    }
  },
  setup() {
    const {showInchValues} = storeToRefs(useUserStore())
    const {setShowInchValues} = useUserStore()
    const {getAltText, getDownloadLink, getFileName} = useDownloadFunctions()
    const {installationImageUrl} = useItemImageUrlHelper()
    return {
      showInchValues,
      setShowInchValues,
      getAltText,
      getDownloadLink,
      getFileName,
      installationImageUrl
    }
  },
  data() {
    return {
      showDimensionsPopup: false
    }
  },
  computed: {
    installationImage() {
      let image = {alternativeText: this.$tv('installationsketch', 'product'), url: null}
      image.url = this.installationImageUrl(this.product, true)
      return image
    },
    isFallbackImage() {
      return this.installationImage.url === FALLBACK_IMAGE
    },
    sortedProductDownloads() {
      const list = [...this.productDownloads]
      return list.sort(function(a, b) {return a.order - b.order})
    }
  },
  methods: {
    toggleShowInchValues() {
      this.setShowInchValues(!this.showInchValues)
    }
  }
}
</script>

<style lang="scss">
.dimension-section {
  .headline {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0;

    h2 {
      margin: 0 0 .5rem;
    }
  }

  .dimensions-container {
    display: flex;
    margin-bottom: 2rem;

    &__dimensions-img-container {
      min-width: 20rem;
      display: flex;
      align-items: center;
      flex-direction: column;
      cursor: pointer;

      &.no-events {
        pointer-events: none;

        img {
          width: 50%;
        }
      }

      img {
        width: 100%;

        @include breakpoint-down($sm) {
          padding-bottom: 1rem;
          display: flex;
          justify-content: center;
        }
      }

      .click-image {
        margin: 0;
        color: $color__secondary;
        font-size: map-get($base-text-button, font-size);
        font-weight: $base-text-font-weight-semi-bold;
      }

      @include breakpoint-down($sm) {
        padding-bottom: 1rem;
        min-width: unset;
      }
    }

    @include breakpoint-down($sm) {
      flex-direction: column;
      width: 100%;
    }

    &__dimensions-details {
      margin-top: 1rem;

      @include breakpoint-up($lg) {
        padding-left: 1rem;
        width: 100%;
      }

      > div {
        font-weight: $base-text-font-weight-semi-bold;
        display: flex;
        justify-content: space-between;
        margin-bottom: .625rem;

        .dimension-label {
          font-weight: $base-text-font-weight-light;
          font-size: map-get($base-text-label-medium, font-size);
        }

        .dimension-measurement {
          min-width: 5rem;
          text-align: right;
          font-size: map-get($base-text-label-medium, font-size);
        }
      }
    }
  }

  .additional-attributes-section {
    .rohs {
      margin-bottom: 1rem;
    }

    .attributes {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-bottom: .625rem;

      .dimension-label {
        font-weight: $base-text-font-weight-light;
      }
    }

    p {
      font-weight: $base-text-font-weight-semi-bold;
      font-size: $base-text-font-size;
      margin: 0;
    }

    p.rohs-header {
      font-weight: $base-text-font-weight-light;
      margin-bottom: .31rem;
      font-size: $base-text-font-size;
    }
  }

  .popup {
    .popup__wrapper {
      width: 100%;
    }

    // Fix for popup-in-popup content spacing
    .popup__content {
      padding: 1.5rem;
    }

    // Ensure proper styling for second layer popups
    &.second-layer {
      .popup__content {
        padding: 1.5rem;
      }
    }

    &-switch {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-bottom: 1.5rem;
    }

    .dimensions-container {
      &__dimensions-img-container {
        width: 100%;
      }

      &__dimensions-details {
        @include breakpoint-up($lg) {
          width: 36.5rem;
        }

        @include breakpoint-down($lg) {
          width: auto;
        }
      }

      padding-bottom: 1rem;
      border-bottom: .125rem solid $color__content--background__secondary;
      margin-bottom: 1rem;
    }

    .bottom-section {
      display: flex;
      justify-content: space-between;

      .additional-attributes-section {
        @include breakpoint-up($lg) {
          width: 100%;
        }

        @include breakpoint-down($lg) {
          width: 50%;
        }

        padding: 0;
      }

      .downloads {
        @include breakpoint-up($lg) {
          min-width: 24rem;
        }

        @include breakpoint-down($lg) {
          width: 50%;
        }

        padding-left: 1rem;

        ul {
          margin-left: .125rem;
          padding-inline-start: 1rem;
          margin-block: .5rem;

          li {
            color: $color__secondary;
          }
        }
      }
    }
  }

  h3 {
      margin: 0 0 .5rem;
    }
}
</style>
